import React from 'react'
import moment from 'moment'
import { css } from '@emotion/css'
import { useIntl, FormattedMessage } from 'react-intl'
import { Dropdown } from '@contrast-security-inc/design-system-inputs'
import { Link, Text } from '@contrast-security-inc/design-system-typography'
import { CompositionBar } from '@contrast-security-inc/design-system-data-bars'
import { Segment } from '@contrast-security-inc/design-system-data-bars/dist/types/CompositionBar/CompositionBar'
import {
  ServerOverviewVulnerabilityProps,
  useServerVulnInfo
} from './api/use-server-vuln-info'
import {
  theme,
  IconWarning
} from '@contrast-security-inc/design-system-foundations'
import { LoadingMaskRectangle } from '../../legacy-design-system/loading-masks'

type VulnSortType = 'severity' | 'status' | 'type'

interface DropdownProps {
  type: VulnSortType
  displayName: string
}

const colors = {
  CRITICAL: 'red50',
  HIGH: 'orange50',
  MEDIUM: 'yellow50',
  LOW: 'neutral40',
  NOTE: 'neutral30'
}

const filterVulnSeverityData = (orgUuid, serverId, vuln) => {
  const vulnDetails = vuln.details.map((vuln, i) => {
    return bodyText(vuln, i)
  })

  const title = `${vuln.severity.toLowerCase()}`

  const tooltipTitle = (
    <FormattedMessage
      id="title_serverVulnerabilities"
      values={{
        type: title.charAt(0).toUpperCase() + title.slice(1)
      }}
    />
  )

  const color: string = colors[vuln.severity]

  return {
    color,
    id: vuln.severity,
    LinkProps: {
      href: `#/${orgUuid}/servers/${serverId}/vulns?severities=${vuln.severity}&statuses=Reported&statuses=Suspicious&statuses=Confirmed`
    },
    TooltipProps: {
      title: tooltipTitle,
      body: vulnDetails
    },
    value: vuln.traces ? vuln.traces : 0
  }
}

const filterVulnStatusData = (orgUuid, serverId, vuln) => {
  if (
    vuln.status === 'Confirmed' ||
    vuln.status === 'Reported' ||
    vuln.status === 'Suspicious'
  ) {
    const vulnDetails = vuln.details.map((vuln, i) => {
      if (vuln.traces > 0) {
        return bodyText(vuln, i)
      }

      return []
    })

    const statusColor = {
      Confirmed: 'red50',
      Reported: 'neutral40',
      Suspicious: 'yellow50'
    }

    return {
      color: statusColor[vuln.status],
      id: vuln.status,
      LinkProps: {
        href: `#/${orgUuid}/servers/${serverId}/vulns?statuses=${vuln.status}`
      },
      TooltipProps: {
        title: `${vuln.status}`,
        body: vulnDetails
      },
      value: vuln.total_vulnerabilities ? vuln.total_vulnerabilities : 0
    }
  }

  return { color: 'neutral40', value: 0, id: vuln.rule_title }
}

const filterVulnTypeData = (allVulns, index, orgUuid, serverId, vuln) => {
  const maxSegments = 8
  const maxListLength = 5

  if (index < maxSegments) {
    const vulnStatus = vuln.details.map((vulnDetail) => {
      if (vulnDetail.traces > 0) {
        return (
          <span key={`${vuln.rule_name}_${vulnDetail.status}`}>
            {vulnDetail.traces} {vulnDetail.status}
            <br></br>
          </span>
        )
      }
    })

    const link = `#/${orgUuid}/servers/${serverId}/vulns?types=${vuln.rule_name}&statuses=Reported&statuses=Suspicious&statuses=Confirmed`

    return {
      color: 'neutral60',
      id: vuln.rule_name,
      LinkProps: {
        href: link
      },
      TooltipProps: {
        title: vuln.rule_title,
        body: vulnStatus
      },
      value: vuln.traces
    }
  }

  if (index + 1 === allVulns.length) {
    const vulnTitlesAndCount = allVulns.reduce((arr, vuln) => {
      arr.push({ title: vuln.rule_title, vulnCount: vuln.traces })

      return arr
    }, [])

    const remainingVulnsShortList = vulnTitlesAndCount.slice(
      maxSegments,
      maxSegments + maxListLength
    )

    const remainingVulnsTooltipText = remainingVulnsShortList.map((vuln) => {
      return (
        <span key={vuln.rule_name}>
          {vuln.title}
          <br></br>
        </span>
      )
    })

    const remainingCount =
      vulnTitlesAndCount.length - remainingVulnsTooltipText.length - maxSegments

    const allVulnNames = allVulns.reduce((arr, vuln) => {
      arr.push(vuln.rule_name)

      return arr
    }, [])

    const remainingTitlesCompleteList = allVulnNames.slice(maxSegments)
    const sortedTypes = remainingTitlesCompleteList.map((name, i) => {
      if (i === 0) {
        return `types=${name}`
      }
      if (i > 0) {
        return `&types=${name}`
      }
    })

    const params = sortedTypes.join('')
    const link = `#/${orgUuid}/servers/${serverId}/vulns?statuses=Reported&statuses=Suspicious&statuses=Confirmed&${params}`

    const remainingTitlesAndVulnCount = vulnTitlesAndCount.slice(maxSegments)

    const totalVulnCount = remainingTitlesAndVulnCount.reduce(
      (acc, current) => {
        return acc + current.vulnCount
      },
      0
    )

    return {
      color: 'neutral30',
      id: 'remaining_vuln_types',
      LinkProps: {
        href: link
      },
      TooltipProps: {
        title: <FormattedMessage id="title_others" />,
        body: (
          <>
            {remainingVulnsTooltipText}
            {remainingCount > 0 && (
              <FormattedMessage
                id="detail_serverVulnerabilityTooltipTitle_type"
                values={{ plus: '+', remainingCount }}
                children={(msg) => <>{msg}</>}
              />
            )}
          </>
        )
      },
      value: totalVulnCount
    }
  }
  return { color: 'neutral40', value: 0, id: vuln.rule_name }
}

const bodyText = (vuln, i) => {
  return (
    <span key={i}>
      {vuln.traces} {vuln.status ? vuln.status : vuln.severity}
      <br></br>
    </span>
  )
}

interface DropdownValueData {
  type: string
  displayName: string
}

export interface SeverityVulnData {
  severity: string
  traces: number
  details: SeverityVulnDataDetail[]
}

export interface SeverityVulnDataDetail {
  status: string
  traces: number
}

export interface StatusVulnData {
  status: string
  total_vulnerabilities: number
  details: []
}

export interface TypeVulnData {
  rule_name: string
  rule_title: string
  traces: number
  details: []
}

const sortSegment = (dropdownValueType, segment) => {
  const sortOrder = {
    red50: 0,
    orange50: 1,
    yellow50: 2,
    neutral40: 3,
    neutral30: 4
  }

  const sortedSegment = segment.sort((element1, element2) => {
    if (dropdownValueType === 'type') {
      return element1.value - element2.value
    }
    return sortOrder[element1.color] - sortOrder[element2.color]
  })

  return sortedSegment as unknown as Segment[]
}

const filterDropdownTypeData = (
  allVulns: SeverityVulnData[] | StatusVulnData[] | TypeVulnData[],
  dropdownValue: DropdownValueData,
  index: number,
  orgUuid: string,
  serverId: number,
  vuln: SeverityVulnData | StatusVulnData | TypeVulnData
) => {
  if (dropdownValue.type === 'severity') {
    return filterVulnSeverityData(orgUuid, serverId, vuln)
  }

  if (dropdownValue.type === 'status') {
    return filterVulnStatusData(orgUuid, serverId, vuln)
  }

  if (dropdownValue.type === 'type') {
    return filterVulnTypeData(allVulns, index, orgUuid, serverId, vuln)
  }

  return { color: 'neutral40', value: 0, id: 'unknownType' }
}

type RenderDropdownCompositionBarProps = {
  orgUuid: string
  serverId: number
}

const RenderDropdownCompositionBar = ({
  orgUuid,
  serverId
}: RenderDropdownCompositionBarProps) => {
  const intl = useIntl()

  const dropdownOptions: DropdownProps[] = [
    {
      type: 'severity',
      displayName: intl.formatMessage({ id: 'action_sortBySeverity' })
    },
    {
      type: 'status',
      displayName: intl.formatMessage({ id: 'action_sortByStatus' })
    },
    {
      type: 'type',
      displayName: intl.formatMessage({ id: 'action_sortByType' })
    }
  ]

  const [dropdownValue, setDropdownValue] = React.useState<DropdownProps>(
    dropdownOptions[0]
  )

  const {
    error,
    isLoading,
    data: serverVulnInfo
  } = useServerVulnInfo({
    orgUuid,
    serverId,
    dropdownValueType: dropdownValue.type ? dropdownValue.type : 'severity'
  })

  const hasError = !!(error || serverVulnInfo?.success === false)

  const filterVulnerabilities = React.useMemo(() => {
    if (serverVulnInfo && serverVulnInfo.total_vulnerabilities > 0) {
      const { traces_breakdown: tracesBreakdown }: any = serverVulnInfo

      const getType = () => {
        const types = {
          severity: tracesBreakdown as SeverityVulnData[],
          status: tracesBreakdown as StatusVulnData[],
          type: tracesBreakdown as TypeVulnData[]
        }

        return types[dropdownValue.type]
      }

      getType()

      const allVulns = tracesBreakdown.filter((vulns) => {
        if (
          (vulns.traces && vulns.traces > 0) ||
          (vulns.total_vulnerabilities && vulns.total_vulnerabilities > 0)
        ) {
          return vulns
        }
      })

      const segment = allVulns.map(
        (
          vuln: SeverityVulnData | StatusVulnData | TypeVulnData,
          index: number
        ) => {
          const dropdownTypeData = filterDropdownTypeData(
            allVulns,
            dropdownValue,
            index,
            orgUuid,
            serverId,
            vuln
          )
          return dropdownTypeData
        }
      )

      return sortSegment(dropdownValue.type, segment)
    }

    if (
      !serverVulnInfo?.total_vulnerabilities ||
      serverVulnInfo?.total_vulnerabilities === 0
    ) {
      const segment = [
        {
          color: 'neutral40',
          id: '1',
          value: intl.formatMessage({
            id: 'detail_serverVulnerabilityCompositionBar_zeroVulnState_Description'
          })
        }
      ]

      return segment as unknown as Segment[]
    }
    return { color: 'neutral40', value: 0, id: 'null' } as unknown as Segment[]
  }, [dropdownValue, serverVulnInfo, orgUuid, serverId, intl])

  if (hasError) {
    return <div>{intl.formatMessage({ id: 'detail_apiRequestError' })}</div>
  }

  return !serverVulnInfo || isLoading ? (
    <>
      <LoadingMaskRectangle
        height={40}
        className={css`
          margin-top: 20px;
        `}
      />
      <LoadingMaskRectangle
        height={39}
        className={css`
          margin-top: 20px;
        `}
      />
    </>
  ) : (
    <>
      <div
        className={css`
          margin: 5px 0 5px 0;
        `}
      >
        <Dropdown<DropdownProps>
          items={dropdownOptions}
          itemToString={(item) => {
            return `${item.displayName}`
          }}
          onChange={(item: any) => {
            if (item.target.value.type) {
              setDropdownValue(item.target.value)
            }
          }}
          value={dropdownValue}
        />
      </div>
      <div>
        <CompositionBar
          className={css`
            ${serverVulnInfo?.total_vulnerabilities
              ? 'width: inherit;'
              : '& > div > div { width: 100% };'}
          `}
          segments={filterVulnerabilities}
          showZeroSegments={
            serverVulnInfo?.total_vulnerabilities ? false : true
          }
          size="lg"
        />
      </div>
    </>
  )
}

export const ServerOverviewVulnerability = ({
  assessServer,
  lastUpdatedTimestamp,
  orgUuid,
  serverId,
  violations,
  vulnCount
}: ServerOverviewVulnerabilityProps) => {
  const fromNow = moment(lastUpdatedTimestamp).fromNow()
  const orgAndServerIdsAvailable = orgUuid && serverId ? true : false

  return (
    <div data-testid="server-overview-section">
      <div>
        <Text font="body" style={{ textTransform: 'none', fontSize: '18px' }}>
          <FormattedMessage
            id="title_numberOfVulnerabilities_Linked"
            values={{
              count: vulnCount,
              vulnCount: (
                <Link
                  href={`#/${orgUuid}/servers/${serverId}/vulns?statuses=Reported&statuses=Suspicious&statuses=Confirmed`}
                  style={{
                    color: theme.color.turquoise70,
                    fontWeight: 'bold',
                    paddingRight: '10px'
                  }}
                >
                  {vulnCount}
                </Link>
              )
            }}
          />
        </Text>
      </div>
      {orgAndServerIdsAvailable && (
        <RenderDropdownCompositionBar orgUuid={orgUuid} serverId={serverId} />
      )}
      {violations && violations.length > 0 && (
        <div
          className={css`
            display: inline-flex;
            padding-top: 5px;
          `}
        >
          <div
            className={css`
              padding: 2px 2px 0 0;
            `}
          >
            <IconWarning color="red50" size="sm" />
          </div>
          <FormattedMessage id="title_policyViolations">
            {(msg) => (
              <span
                className={css`
                  color: ${theme.color.red50};
                `}
              >
                {msg}
              </span>
            )}
          </FormattedMessage>
        </div>
      )}
      {!assessServer && vulnCount && vulnCount > 0 && (
        <div>
          <FormattedMessage id="title_serverOverview_assessLastUpdated_time">
            {(msg) => (
              <span
                className={css`
                  color: ${theme.color.neutral60};
                  font-size: 12px;
                `}
              >
                {msg} {fromNow}
              </span>
            )}
          </FormattedMessage>
        </div>
      )}
    </div>
  )
}
